import { Box } from "@mui/material";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRequestsContext } from "../../../../contexts/RequestsContext";
import type { OpenShiftRequest, ReplaceShiftRequest } from "../../../../utils/models";
import { getRespondedRequestData } from "../../../../utils/utilFunctions";
import { InlineHint } from "../../../UIToolkit/molecules/InlineHint";
import { SheetStep } from "../../../UIToolkit/Popups/SheetStep";
import { SheetStepButton } from "../../../UIToolkit/Popups/SheetStepButton";
import { closeGapsSheetIfCurrent, type GapsSheetIntentOf, type GapsSheetKind } from "../useGapsSheet";
import { APPROVE_SHEET, GapsApproveConfirmStep } from "./GapsApproveConfirmStep";
import { GapsSheetFrame } from "./GapsSheetFrame";
import { useSheetOpening } from "./useSheetOpening";

type Target = GapsSheetIntentOf<GapsSheetKind.ApproveConfirm>;

type Props = {
	/** The store's own intent object, or `null` when the sheet is not up (see `useSheetOpening`). */
	target: Target | null;
};

const SHEET = APPROVE_SHEET;

/**
 * "Give it to Jane?" — the confirm every approval on the gaps view goes through first (P3-6, the decision "Approve:
 * always confirm first, then the assign-unit sheet").
 *
 * It names the person, the shift and what approving does to everyone else who asked: when this approval fills the
 * request, the others are declined (`approveDeclineCount`); short of that they stay in line for the openings left. It
 * deliberately does not promise that every one of them is told — one case declines without a notification.
 *
 * Confirming runs the desktop's approval gates and the write (`useGapsApprove`), then moves to the request's day on the
 * home — as the classic approve does — and opens the assign-unit sheet for the new shift, whose "Assign later" leaves
 * the person on the shift without a unit.
 */
export const GapsApproveSheet: FC<Props> = ({ target }) => {
	const { shown, openingKey } = useSheetOpening(target);
	const { staffRequests } = useRequestsContext();
	const live = useMemo(() => (shown === null ? undefined : staffRequests.find((request) => request.id === shown.requestId)), [shown, staffRequests]);
	// Closes THIS opening only: an approval that finishes after the user moved on must not close what they moved to.
	const close = useCallback((): void => {
		if (shown !== null) closeGapsSheetIfCurrent(shown);
	}, [shown]);

	return (
		<GapsSheetFrame open={target !== null} onClose={close} dataTestId={`${SHEET}-sheet`}>
			{shown !== null && openingKey !== null && <GapsApproveFlow key={openingKey} opening={shown} live={live} onClose={close} />}
		</GapsSheetFrame>
	);
};

type FlowProps = {
	/** The intent this opening is for — what the approval hands on from. */
	opening: Target;
	/** The request as the live list has it now; `undefined` once it left the list (or before it was ever found). */
	live: OpenShiftRequest | ReplaceShiftRequest | undefined;
	onClose: () => void;
};

/**
 * One opening's confirm. The request is held once found, and HELD STILL from the tap until the approval ends without
 * applying anything: the write itself closes the request or moves the person's answer on, and the step must not flip to
 * "moved on" or "gone" while it writes or while the drawer animates out after handing on. Keyed by the opening, so a new
 * one starts from the live request again.
 */
const GapsApproveFlow: FC<FlowProps> = ({ opening, live, onClose }) => {
	const { t } = useTranslation();
	const [held, setHeld] = useState<OpenShiftRequest | ReplaceShiftRequest | undefined>(live);
	const [isHeldStill, setIsHeldStill] = useState<boolean>(false);
	if (!isHeldStill && live !== undefined && live !== held) setHeld(live);
	const response = held === undefined ? undefined : getRespondedRequestData(held).find((entry) => entry.nurse_id === opening.nurseId);

	if (held === undefined || response === undefined) {
		return (
			<SheetStep
				stepKey={`${SHEET}-gone`}
				dataTestId={SHEET}
				title={t("gaps.approve.goneTitle")}
				footer={
					<SheetStepButton tone="dismiss" onClick={onClose} dataTestId={`${SHEET}-cancel`}>
						{t("close")}
					</SheetStepButton>
				}
			>
				<Box sx={{ px: 2, py: 2 }}>
					<InlineHint tone="warning" dataTestId={`${SHEET}-gone`}>
						{t("gaps.approve.gone")}
					</InlineHint>
				</Box>
			</SheetStep>
		);
	}
	return (
		<GapsApproveConfirmStep
			opening={opening}
			request={held}
			response={response}
			isRequestLive={isHeldStill || live !== undefined}
			onHoldChange={setIsHeldStill}
			onClose={onClose}
		/>
	);
};
